import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Download, Instagram, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { dealerLaunchCard, dealerIgPostLaunch, dealerIgStatus } from "@/lib/api";

// "I'm live with OreVital" share card — shown right after the launch fireworks.
export default function LaunchShareDialog({ open, onOpenChange, recId }) {
  const [fmt, setFmt] = useState("square");
  const [card, setCard] = useState(null);
  const [caption, setCaption] = useState("");
  const [ig, setIg] = useState(null);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !recId) return;
    setCard(null);
    dealerLaunchCard(recId, fmt).then((r) => { setCard(r); setCaption((c) => c || r.caption || ""); }).catch(() => toast.error("Could not build your card"));
  }, [open, recId, fmt]);
  useEffect(() => { if (open && recId) dealerIgStatus(recId).then(setIg).catch(() => setIg(null)); }, [open, recId]);

  async function postToIg() {
    setPosting(true);
    try {
      const r = await dealerIgPostLaunch(recId, card.image_url, caption, fmt);
      setPosted(r);
      toast.success(`Posted to @${r.username}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Instagram post failed");
    } finally {
      setPosting(false);
    }
  }
  function copyCaption() {
    navigator.clipboard.writeText(caption).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="launch-share-dialog">
        <DialogHeader>
          <DialogTitle>Share the news</DialogTitle>
          <DialogDescription>Your ready-to-post "I'm live with OreVital" card. Download it or post straight to Instagram.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 rounded-lg bg-zinc-100 p-0.5 w-fit" data-testid="launch-share-fmt">
          {[["square", "Feed 1:1"], ["story", "Story 9:16"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setFmt(v)} className={`px-3 h-8 rounded-md text-xs font-semibold ${fmt === v ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`} data-testid={`launch-share-fmt-${v}`}>{l}</button>
          ))}
        </div>
        <div className={`mx-auto w-full ${fmt === "story" ? "max-w-[260px]" : "max-w-[360px]"} rounded-xl overflow-hidden bg-[#0b2545] ${fmt === "story" ? "aspect-[9/16]" : "aspect-square"} flex items-center justify-center`}>
          {card ? <img src={card.image_url} alt="I'm live with OreVital" className="w-full h-full object-contain" data-testid="launch-share-image" /> : <Loader2 className="animate-spin text-white/70" />}
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-zinc-700">Caption</span>
            <button type="button" onClick={copyCaption} className="text-xs text-violet-700 font-semibold inline-flex items-center gap-1" data-testid="launch-share-copy">
              {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <Textarea rows={4} value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-xl text-sm" data-testid="launch-share-caption" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <a href={card?.image_url} download={`orevital-live-${fmt}.jpg`} target="_blank" rel="noreferrer"
            className={`h-11 rounded-xl border border-zinc-200 bg-white text-zinc-800 font-semibold text-sm inline-flex items-center justify-center gap-1.5 ${!card ? "pointer-events-none opacity-50" : "hover:bg-zinc-50"}`} data-testid="launch-share-download">
            <Download size={15} /> Download
          </a>
          {posted ? (
            <a href={posted.permalink} target="_blank" rel="noreferrer" className="h-11 rounded-xl bg-emerald-600 text-white font-semibold text-sm inline-flex items-center justify-center gap-1.5" data-testid="launch-share-view-post">
              <ExternalLink size={15} /> View on Instagram
            </a>
          ) : ig?.connected ? (
            <Button onClick={postToIg} disabled={!card || posting} className="h-11 rounded-xl bg-gradient-to-r from-pink-500 via-fuchsia-500 to-amber-400 text-white font-semibold" data-testid="launch-share-post-ig">
              {posting ? <Loader2 className="animate-spin" size={15} /> : <><Instagram size={15} className="mr-1.5" /> Post to @{ig.username}</>}
            </Button>
          ) : (
            <Button variant="outline" disabled className="h-11 rounded-xl" title="Connect Instagram in Setup → Connect" data-testid="launch-share-ig-disabled">
              <Instagram size={15} className="mr-1.5" /> Connect Instagram to post
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
